import type { Dispatch, SetStateAction } from 'react';
import { breakpoints, Button, Inline, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { X } from '@phosphor-icons/react';
import { useFormContext } from 'react-hook-form';
import { useMediaQuery } from 'usehooks-ts';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { screenReadersOnlyStyles } from '../../../utils/styleUtils';
import {
  DEPARTURE_DATE,
  DESTINATION,
  MULTI_BOUND,
  ORIGIN,
  SINGLE_BOUND,
} from '../constants/formFields';
import type { DateFormat } from '../types/dateFormat';
import DatesNew from './DatesNew';
import OriginAndDestinationNew from './OriginAndDestinationNew';
import ZeroWidthSpace from './ZeroWidthSpace';

const buttonSizeStyles = css`
  height: 3rem;
  width: 3rem;
`;

const removeTripIconStyles = css`
  height: 1.5rem;
  width: 1.5rem;
`;

const multiboundOriginAndDestinationStyles = css`
  flex: 2;
`;

type BoundNewProps = {
  boundIndex: number;
  dateFormat: DateFormat;
  hasRemoveButtons?: boolean;
  onRemove?: (boundId?: number) => void;
  selectedTripType?: TripType;
  setAreOriginAndDestinationFilled?: Dispatch<SetStateAction<boolean>>;
};

const getFieldName = (fieldName: string, selectedTripType?: TripType, index?: number) =>
  selectedTripType !== tripTypes.MULTI_STOP
    ? `${SINGLE_BOUND}.${fieldName}`
    : `${MULTI_BOUND}[${index}].${fieldName}`;

const BoundNew = ({
  boundIndex,
  dateFormat,
  hasRemoveButtons,
  onRemove,
  selectedTripType,
  setAreOriginAndDestinationFilled,
}: BoundNewProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);

  const getName = (fieldName: string) => getFieldName(fieldName, selectedTripType, boundIndex);
  const isRemovableTrip = boundIndex ? boundIndex > 1 : false;
  const [origin, destination] = watch([getName(ORIGIN), getName(DESTINATION)]);

  return (
    <div data-testid="bound">
      {selectedTripType !== tripTypes.MULTI_STOP ? (
        <Inline
          collapseBelow={breakpoints._768}
          spacing={{
            [breakpoints._0]: 24,
            [breakpoints._768]: 0,
          }}
        >
          <OriginAndDestinationNew
            boundIndex={boundIndex}
            destinationName={getName(DESTINATION)}
            originName={getName(ORIGIN)}
            setAreOriginAndDestinationFilled={setAreOriginAndDestinationFilled}
          />
        </Inline>
      ) : (
        <Inline collapseBelow={breakpoints._768} spacing={24}>
          <Inline
            className={multiboundOriginAndDestinationStyles}
            collapseBelow={breakpoints._768}
            spacing={{
              [breakpoints._0]: 24,
              [breakpoints._768]: 0,
            }}
          >
            <OriginAndDestinationNew
              boundIndex={boundIndex}
              destinationName={getName(DESTINATION)}
              originName={getName(ORIGIN)}
            />
          </Inline>
          <DatesNew
            boundIndex={boundIndex}
            dateFormat={dateFormat}
            departureDateName={getName(DEPARTURE_DATE)}
            selectedTripType={selectedTripType}
          />
          {isWiderViewport && (isRemovableTrip || hasRemoveButtons) && (
            <Stack spacing={4}>
              <ZeroWidthSpace />
              {isRemovableTrip ? (
                <Button
                  className={buttonSizeStyles}
                  data-testid={`searchForm-removeTrip-${boundIndex}-non-mobile-button`}
                  onClick={() => onRemove?.(boundIndex)}
                >
                  <span className={screenReadersOnlyStyles}>
                    {origin && destination
                      ? t('ScreenReader.SearchForm.RemoveTrip.Label', origin.city, destination.city)
                      : t('Searchform.RemoveBound.Button.Text')}
                  </span>
                  <X
                    aria-hidden
                    className={removeTripIconStyles}
                    color="currentColor"
                    weight="regular"
                  />
                </Button>
              ) : (
                <>{hasRemoveButtons && <div className={buttonSizeStyles} />}</>
              )}
            </Stack>
          )}
        </Inline>
      )}
    </div>
  );
};

export default BoundNew;
